import { Injectable } from '@angular/core';
import { HvlSecurityService } from '@hvlng/framework-bff/security';
import { BehaviorSubject, map, Observable, of, switchMap } from 'rxjs';
import { PersonnelData } from '../model/personnel-data';
import { PerEmployeeCardsService } from '../per-employee-cards/service/per-employee-cards-service';
import { PerEmployeeCardsQueryModel } from '../per-employee-cards/model/per-employee-cards-query-model';
import { HrAuthority } from '../constant/authority/hr-authority';
import { EmployeeCardLocalService } from './employeeCardLocalService';
import { UiToastMessageService, UiTranslateLoaderService } from 'ui-shared';

@Injectable({
    providedIn: 'root',
})
export class SelectedPersonnelService {
    private sourcePeople = new BehaviorSubject<PersonnelData>(null);
    dataPeople$ = this.sourcePeople.asObservable();

    constructor(
        private securityService: HvlSecurityService,
        private employeeCardsService: PerEmployeeCardsService,
        private employeeCardLocalService: EmployeeCardLocalService,
        private uiToastMessageService: UiToastMessageService,
        private translateService: UiTranslateLoaderService,
    ) {
        this.securityService.isUserLoggedOut.subscribe({
            next: () => {
                this.clearData();
            },
        });
    }

    public isSelf(): boolean {
        return this.sourcePeople.value?.isSessionPersonnel;
    }

    public isSelected(): boolean {
        return !this.isSelf() && !!this.sourcePeople.value?.identificationNumber && this.sourcePeople.value?.extraData;
    }

    public isSelfOrAdmin(adminAuthority: string): boolean {
        return this.isSelf() || this.securityService.hasAuthority(adminAuthority);
    }

    public isAdminAndNotSelf(...adminAuthorities: string[]): boolean {
        adminAuthorities.push(HrAuthority.Personnel.Admin.BASE);
        return !this.isSelf() && adminAuthorities.some((auth) => this.securityService.hasAuthority(auth));
    }

    public isAdmin(...adminAuthorities: string[]): boolean {
        return (
            this.securityService.hasAuthority(HrAuthority.Personnel.Admin.BASE) ||
            adminAuthorities.some((auth) => this.securityService.hasAuthority(auth))
        );
    }

    public getPersonnelData(): PersonnelData {
        return this.sourcePeople.value;
    }

    public saveData(data: PersonnelData) {
        this.employeeCardLocalService.getEmployeeCards().subscribe((employeeCards) => {
            if (employeeCards) {
                if (!data.employeeSubTypeId) {
                    this.uiToastMessageService.showWarn(this.translateService.instant('perEmployeeCard.personnelEmployeeSubTypeIdEmptyMessage'));
                    return;
                }

                employeeCards.forEach((employeeCard) => {
                    employeeCard.isVisibleToEmployee = employeeCard.perEmployeeCardsDetails && employeeCard.perEmployeeCardsDetails.some(p => p.employeeSubtypeCodeTextId === data.employeeSubTypeId);
                });

                data.cardInfoList = employeeCards;
            }

            // personnel-manager-panel üzerinden bu servis çağrıldığında card bilgisi dolu geldiği için tekrar burada çekilmiyor.
            // bu servisin kullanıldığı farklı yerlerde olduğu için boşsa burada dolduruluyor.
            if (data.authorizedCards) {
                this.sourcePeople.next(data);
            } else {
                this.employeeCardsService
                    .getAuthorizedEmployeeCardsOfSessionPersonnel(data.peopleId)
                    .subscribe((resCards) => {
                        data.authorizedCards = resCards;
                        this.sourcePeople.next(data);
                    });
            }
        });
    }

    public saveDataObservable(data: PersonnelData, includeCardCodeTextData: boolean | null = null): Observable<PersonnelData> {

        let qm: PerEmployeeCardsQueryModel = new PerEmployeeCardsQueryModel();
        qm.peopleId = data.peopleId;
        if (includeCardCodeTextData)
            qm.includeCardCodeTextData = true;
        return this.employeeCardsService.queryList(qm).pipe(
            switchMap((employeeCards) => {
                if (employeeCards) {
                    if (includeCardCodeTextData) {


                        if (!data.employeeSubTypeId) {
                            this.uiToastMessageService.showWarn(this.translateService.instant('perEmployeeCard.personnelEmployeeSubTypeIdEmptyMessage'));
                            return;
                        }

                        employeeCards.forEach((employeeCard) => {
                            employeeCard.isVisibleToEmployee = employeeCard.perEmployeeCardsDetails && employeeCard.perEmployeeCardsDetails.some(p => p.employeeSubtypeCodeTextId === data.employeeSubTypeId);
                        });
                    }

                    data.cardInfoList = employeeCards;
                }

                if (data.authorizedCards) {
                    return of(data);
                } else {
                    return this.employeeCardsService
                        .getAuthorizedEmployeeCardsOfSessionPersonnel(data.peopleId)
                        .pipe(
                            map((resCards) => {
                                data.authorizedCards = resCards;
                                this.sourcePeople.next(data);
                                return data;
                            })
                        );
                }
            })
        );
    }


    public clearData() {
        this.sourcePeople.next(null);
    }
}
